import { useEffect, useState, type ReactNode } from 'react'
import { api, type LayoutAsset } from '../lib/api'
import { colourChoices, paperOnly, strayColour, styleControls } from './controls'
import { MAX_BORDER_WIDTH, elementName, qrFormatPatch } from './editing'
import { t, type StaffKey } from '../lib/staffText'
import { useCustomerLanguages } from '../lib/i18n/useCustomerLanguages'
import { nativeName } from '../lib/i18n/customer'
import { designLanguages } from './designLanguages'
import { WordFields, textLabel } from './WordFields'
import { PREVIEW_LANGUAGE } from './sample'
import { uploadRefusal } from '../lib/signageRefusals'
import { MAX_SCREEN_SIDE, MIN_SCREEN_SIDE, presetLabel, presetsFor } from './canvasSize'
import { deletePage, pageLabel, renamePage, withPages } from './pages'
import { textIn, type ButtonAction } from './types'
import { DEFAULT_MIN_SCALE as DEFAULT_SHRINK, fitsRows, minScaleOf } from './fit'

/** The shrink floors offered for auto-fit, as shares of the set size. */
const SHRINK_STEPS = [1, 0.8, 0.6, 0.45, 0.3] as const
import { fontOptions, hasText, withElementFont } from './fonts'
import { DEFAULT_DATE_FORMAT, dateFormatChoices, screenDateChoices } from './dateFormat'
import { MAX_RULE_THICKNESS, ruleThickness } from './rule'
import { MediaInspector } from './MediaInspector'
import { ColourPicker } from './ColourPicker'
import { mediaOffered } from '../lib/signage'
import { slipPicture } from '../lib/pictures'
import type { MediaTarget } from './media'
import { replaceGrid } from './gridButtons'
import { colour, placeholderKeys } from './types'
import type { CanvasTheme, ElementMotion, LayoutDocument, LayoutElement, LayoutText, MediaItem, Screen } from './types'
import { COUNTER_HOME, COUNTER_SCREENS } from './counter'
import { motionChoices, motionOf, screenChangeOf, withMotion } from './counterMotion'
import { Button, Input, Label, Slider, Switch, TextArea, TextField, ToggleButton, ToggleButtonGroup } from '@heroui/react'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { IconButton, Notice, StudioFilePicker, StudioSelect, Tick } from './heroParts'
import { serviceName, t as kioskSay, type Language } from '../lib/kioskText'
import type { Service } from '../lib/types'
import { SameSwitch } from '../ui/SameSwitch'
import { isSame, ownWords, withWords } from '../lib/i18n/same'
import '../styles/studio.css'


/**
 * The panel for the selected element.
 *
 * Numbers for position and size, because a customer aligning two things wants to
 * type the same number twice rather than drag until it looks right. Colours are
 * offered as the design system's tokens, drawn as swatches in the design's own
 * theme — and a literal is possible behind the last swatch but is not what the row
 * suggests: a layout built from tokens follows the theme it is shown in, and one
 * built from literals does not.
 */

/**
 * What each row limit does, said where the choice is made.
 *
 * "No limit" is the one worth spelling out. It reads as the safe answer and is not
 * one: with nothing to stop it, a long value is set smaller and smaller until it
 * fits its box, and a thermal head prints small grey shapes rather than small
 * letters. A customer holding that slip cannot read their own name on it.
 */
const ROW_HINTS: Record<number, StaffKey> = {
  0: 'designs.insp.rows0',
  1: 'designs.insp.rows1',
  2: 'designs.insp.rows2',
  3: 'designs.insp.rows3',
}

const POSITION_KEYS: Record<'x' | 'y' | 'w' | 'h', StaffKey> = {
  x: 'designs.insp.fromLeft',
  y: 'designs.insp.fromTop',
  w: 'designs.insp.width',
  h: 'designs.insp.height',
}

const SCREEN_NAMES: Record<Screen, StaffKey> = {
  attract: 'designs.ed.welcome',
  choosing: 'designs.ed.choosing',
  issued: 'designs.ed.ticket',
  called: 'designs.ed.called',
  free: 'designs.ed.free',
  board: 'designs.ed.default',
  call: 'designs.ed.called',
}
const KIOSK_SCREENS: Screen[] = ['attract', 'choosing', 'issued']

export function Inspector({
  document: doc,
  element,
  onChange,
  onReplace,
  onDelete,
  onDuplicate,
  onCopy,
  onDone,
  onAddMediaItem,
  onSetPicture,
  services,
  language,
}: {
  document: LayoutDocument
  /** With nothing selected the panel is DesignPanel's. */
  element: LayoutElement
  /** The language chosen under the canvas: the one words are edited in. */
  language: string
  onChange: (element: LayoutElement) => void
  /** Replaces the whole design, for a change that reaches past one element: a
   *  page renamed or deleted, a style copied to every button on a page. */
  onReplace: (doc: LayoutDocument) => void
  onDelete: (id: string) => void
  /** A copy of the element beside it, chosen. */
  onDuplicate?: (id: string) => void
  /** Keeps the element to paste into this or another design. */
  onCopy?: (id: string) => void
  /** Lets go of the selection, back to the screen's own settings. */
  onDone: () => void
  /** Adds a finished upload to a playlist in the document as it is by then. */
  onAddMediaItem: (elementId: string, target: MediaTarget, item: MediaItem) => void
  /** Makes a picture draw a finished upload, in the document as it is by then.
   *  Like onAddMediaItem, the one from the render the upload started in is called:
   *  it knows which design that was. */
  onSetPicture: (elementId: string, assetId: string) => void
  /** The organisation's catalogue, for a service grid. Undefined while it loads. */
  services?: Service[]
}) {
  const [assets, setAssets] = useState<LayoutAsset[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const { say, lang: reader } = useStaffText()
  // A same text's copy and its value when switched off go to the design's first tab.
  const tabs = designLanguages(doc, useCustomerLanguages())
  const several = tabs.length > 1
  const defaultLang = tabs[0]!

  useEffect(() => {
    if (element.kind !== 'image' && element.kind !== 'button' && element.kind !== 'logo') return
    api
      .assets()
      .then((r) => setAssets(r.assets))
      .catch(() => setAssets([]))
  }, [element.kind])

  // A service grid is kept only for old designs: its one action is to become buttons.
  if (element.kind === 'services') {
    return (
      <section className="studio-panel">
        <PanelHead title={elementName(element, doc, reader)} onDone={onDone} />
        <Button variant="secondary" size="sm" isDisabled={!services} onPress={() => services && onReplace(replaceGrid(doc, element.id, services))}>
          {say('designs.insp.replaceGrid')}
        </Button>
      </section>
    )
  }

  const set = (patch: Partial<LayoutElement>) => onChange({ ...element, ...patch })
  // Which values this element's words stand in for, if any. It decides whether a
  // sample is worth asking for: an element with no placeholder in it says the same
  // thing on every ticket, and there is nothing to preview.
  const bound = element.kind === 'text' ? Object.values(element.content ?? {}).flatMap(placeholderKeys) : []
  const setStyle = (patch: Partial<NonNullable<LayoutElement['style']>>) =>
    onChange({ ...element, style: { ...element.style, ...patch } })
  const setOptions = (patch: Partial<NonNullable<LayoutElement['options']>>) =>
    onChange({ ...element, options: { ...element.options, ...patch } })
  // Only the settings this kind's renderers read. A hidden field leaves no gap.
  const controls = styleControls(element.kind)
  const theme = doc.canvas.theme ?? 'dark'

  async function upload(file: File) {
    // Taken now, not when the file has arrived: the designer may select another
    // element, or open another design, while it is on its way.
    const elementId = element!.id
    const setPictureLater = onSetPicture
    setUploading(true)
    setUploadError('')
    try {
      // Asked for as a picture, so the server refuses a film before storing it.
      // A slip's picture is shrunk to the paper first: it travels with every ticket.
      const asset =
        doc.kind === 'ticket'
          ? await api.uploadAsset(await slipPicture(file), 'slip')
          : await api.uploadAsset(file, 'picture')
      // Films share the asset list now, and the server takes one wherever the
      // licence covers signage. A picture element drawing a film is a broken
      // image on a wall, so a film is refused here, where the choice was made,
      // rather than at Publish (the server refuses it there too).
      if (!asset.contentType.startsWith('image/')) {
        setUploadError(say('designs.insp.isFilm'))
        return
      }
      setAssets((current) => [asset, ...current.filter((a) => a.id !== asset.id)])
      setPictureLater(elementId, asset.id)
    } catch (err) {
      setUploadError(uploadRefusal(err, reader))
    } finally {
      setUploading(false)
    }
  }

  const number = (label: string, value: number, onValue: (n: number) => void, extra?: { min?: number; max?: number }) => (
    <NumberInput label={label} value={value} min={extra?.min} max={extra?.max} onValue={onValue} />
  )

  // What a text or button says in the chosen language names its panel.
  const says = element.kind === 'text' || element.kind === 'button' ? ownWords(element.content, language).trim() : ''

  return (
    <section className="studio-panel">
      <PanelHead title={says || elementName(element, doc, reader)} named={says || undefined} onDone={onDone} />

      {element.kind === 'text' && (
        <>
          <CustomerWords
            label={say('designs.insp.text')}
            text={element.content}
            language={language}
            several={several}
            defaultLang={defaultLang}
            onChange={(content) => set({ content })}
          />
          {bound.length > 0 && (
            <TextField className="studio-field" value={element.previewText ?? ''} onChange={(v) => set({ previewText: v })}>
              {/* The slip that comes out wrong is the one issued to somebody with four names. */}
              <Label title={say('designs.insp.previewHint', { placeholder: `{{${bound[0]}}}` })}>{say('designs.insp.previewText')}</Label>
              <Input variant="secondary" placeholder={say('designs.insp.previewSample')} />
            </TextField>
          )}
        </>
      )}

      {element.kind === 'ticker' && (
        <CustomerWords
          label={say('designs.insp.text')}
          hint={say('designs.insp.tickerHint')}
          multiline
          text={element.content}
          language={language}
          several={several}
          defaultLang={defaultLang}
          onChange={(content) => set({ content })}
        />
      )}

      {doc.kind === 'ticket' && fitsRows(element.kind) && (
        <Group label={say('designs.insp.rows')} hint={say(ROW_HINTS[element.options?.maxRows ?? 0]!)} warn={(element.options?.maxRows ?? 0) === 0}>
          <Seg
            label={say('designs.insp.rows')}
            value={String(element.options?.maxRows ?? 0)}
            options={[
              ['0', say('designs.insp.noLimit')],
              ['1', '1'],
              ['2', '2'],
              ['3', '3'],
            ]}
            onChange={(value) => setOptions({ maxRows: (Number(value) || undefined) as 1 | 2 | 3 | undefined })}
          />
        </Group>
      )}

      {fitsRows(element.kind) && !element.options?.maxRows && (
        <Group label={say('designs.insp.shrinkTo')}>
          <Seg
            label={say('designs.insp.shrinkTo')}
            value={String(minScaleOf(element.options?.minScale))}
            options={SHRINK_STEPS.map((v) => [String(v), `${Math.round(v * 100)}%`])}
            onChange={(value) => setOptions({ minScale: Number(value) === DEFAULT_SHRINK ? undefined : Number(value) })}
          />
        </Group>
      )}

      {(element.kind === 'image' || element.kind === 'logo') && (
        <>
          <StudioSelect
            label={say(element.kind === 'logo' ? 'designs.el.logo' : 'designs.el.image')}
            value={element.assetId ?? ''}
            // A logo with no picture of its own is empty, not a fault.
            onChange={(value) => set({ assetId: value || (element.kind === 'logo' ? undefined : value) })}
            options={[
              { value: '', label: say(element.kind === 'logo' ? 'designs.look.noLogo' : 'designs.insp.choose') },
              ...assets
                .filter((asset) => asset.contentType.startsWith('image/'))
                .map((asset) => ({ value: asset.id, label: `${asset.filename} · ${asset.width}×${asset.height}` })),
            ]}
          />
          <div className="studio-field">
            <StudioFilePicker
              accept="image/png,image/jpeg,image/gif,.png,.jpg,.jpeg,.gif"
              label={say('designs.media.upload')}
              disabled={uploading}
              onFile={(file) => void upload(file)}
              tip={say('designs.insp.pictureHint')}
            />
          </div>
          {uploadError && <Notice>{uploadError}</Notice>}
        </>
      )}

      {/* Keyed by the element, so selecting another playlist is another panel,
          and an upload begun in one can never land in the next. */}
      {element.kind === 'media' && mediaOffered(doc.kind) && (
        <MediaInspector key={element.id} element={element} onChange={onChange} onAddItem={onAddMediaItem} />
      )}

      {element.kind === 'button' && (
        <ButtonFields
          element={element}
          document={doc}
          services={services}
          assets={assets}
          uploading={uploading}
          uploadError={uploadError}
          onUpload={(file) => void upload(file)}
          onChange={onChange}
          language={language}
          several={several}
          defaultLang={defaultLang}
        />
      )}

      <WordFields document={doc} element={element} language={language} onReplace={onReplace} />

      {hasText(element.kind) && (
        <StudioSelect
          label={say('designs.insp.font')}
          value={element.font ?? ''}
          onChange={(value) => onChange(withElementFont(element, value))}
          options={[
            { value: '', label: say('designs.insp.designFont') },
            ...fontOptions(say('designs.font.standard'), say('designs.font.display')),
          ]}
        />
      )}

      {controls.size && (
        <Slider
          className="studio-slider"
          minValue={0.25}
          maxValue={4}
          step={0.05}
          value={element.style?.scale ?? 1}
          onChange={(v) => setStyle({ scale: Array.isArray(v) ? v[0] : v })}
        >
          <span className="studio-between">
            <Label>{say('designs.insp.size')}</Label>
            <Slider.Output className="studio-muted">{() => `${Math.round((element.style?.scale ?? 1) * 100)}%`}</Slider.Output>
          </span>
          <Slider.Track>
            <Slider.Fill />
            <Slider.Thumb />
          </Slider.Track>
        </Slider>
      )}

      {(controls.align || controls.weight) && (
        <div className="studio-panel__pair">
          {controls.align && (
            <Group label={say('designs.insp.align')}>
              <Seg
                label={say('designs.insp.align')}
                value={element.style?.align ?? 'left'}
                options={[
                  ['left', say('designs.insp.left')],
                  ['center', say('designs.insp.centre')],
                  ['right', say('designs.insp.right')],
                ]}
                onChange={(value) => setStyle({ align: value as 'left' | 'center' | 'right' })}
              />
            </Group>
          )}
          {controls.weight && (
            <Group label={say('designs.insp.weight')}>
              <Seg
                label={say('designs.insp.weight')}
                value={String(element.style?.weight ?? 400)}
                options={[
                  ['400', say('designs.insp.regular')],
                  ['500', say('designs.insp.medium')],
                  ['700', say('designs.insp.bold')],
                ]}
                onChange={(value) => setStyle({ weight: Number(value) })}
              />
            </Group>
          )}
        </div>
      )}

      {element.kind === 'rule' && !paperOnly(doc.kind) && (
        <Toggle label={say('designs.insp.gradient')} checked={element.style?.gradient === true} onChange={(on) => setStyle({ gradient: on || undefined })} />
      )}
      {element.kind === 'rule' &&
        number(
          say('designs.insp.thickness'),
          element.options?.thickness ?? ruleThickness(element, paperOnly(doc.kind)),
          (n) => setOptions({ thickness: Math.min(Math.max(n, 1), MAX_RULE_THICKNESS) }),
          { min: 1, max: MAX_RULE_THICKNESS },
        )}
      {element.kind === 'panel' && (
        <>
          {/* Paper prints an outline only (layout.panelProblems). */}
          {!paperOnly(doc.kind) && (
            <Swatches label={say('designs.insp.fill')} theme={theme} value={element.style?.background ?? ''} none={say('designs.insp.none')} onPick={(value) => setStyle({ background: value || undefined })} />
          )}
          <Swatches label={say('designs.insp.border')} theme={theme} paper={paperOnly(doc.kind)} value={element.style?.border ?? ''} none={paperOnly(doc.kind) ? null : say('designs.insp.none')} onPick={(value) => setStyle({ border: value || undefined })} />
          <div className="studio-row2">
            {number(say('designs.insp.borderWidth'), element.style?.borderWidth ?? 2, (n) => setStyle({ borderWidth: Math.min(Math.max(n, 1), MAX_BORDER_WIDTH) }), { min: 1, max: MAX_BORDER_WIDTH })}
            {number(say('designs.insp.corners'), element.style?.radius ?? 0, (n) => setStyle({ radius: Math.max(0, n) || undefined }), { min: 0, max: 200 })}
          </div>
          {!paperOnly(doc.kind) && (
            <Toggle label={say('designs.insp.shadow')} checked={element.style?.shadow === true} onChange={(on) => setStyle({ shadow: on || undefined })} />
          )}
        </>
      )}
      {element.kind === 'queues' && (
        <Group label={say('designs.insp.shows')}>
          {/* Size means a different thing in each view, so a switch starts it at 100%. */}
          <Seg
            label={say('designs.insp.shows')}
            value={element.options?.view ?? 'counts'}
            options={[
              ['counts', say('designs.insp.viewCounts')],
              ['grid', say('designs.insp.viewGrid')],
            ]}
            onChange={(value) => {
              if (value === (element.options?.view ?? 'counts')) return
              onChange({
                ...element,
                options: { ...element.options, view: value === 'grid' ? 'grid' : undefined },
                style: { ...element.style, scale: undefined },
              })
            }}
          />
        </Group>
      )}
      {element.kind === 'dateTime' && (
        <StudioSelect
          label={say('designs.insp.shows')}
          value={element.options?.format || DEFAULT_DATE_FORMAT}
          onChange={(value) => setOptions({ format: value })}
          options={
            paperOnly(doc.kind)
              ? dateFormatChoices(element.options?.format).map((f) => ({ value: f.value, label: f.label ? say(f.label) : f.value }))
              : screenDateChoices(element.options?.format, new Date(), language)
          }
        />
      )}
      {controls.colour && !element.style?.gradient && (
        <Swatches label={say('designs.insp.colour')} theme={theme} paper={paperOnly(doc.kind)} value={element.style?.colour ?? ''} onPick={(value) => setStyle({ colour: value })} />
      )}
      {controls.background && (
        <Swatches label={say('designs.insp.background')} theme={theme} paper={paperOnly(doc.kind)} value={element.style?.background ?? ''} onPick={(value) => setStyle({ background: value })} />
      )}
      {element.kind === 'button' && (
        <>
          <Swatches label={say('designs.insp.border')} theme={theme} value={element.style?.border ?? ''} none={say('designs.insp.none')} onPick={(value) => setStyle({ border: value || undefined })} />
          <div className="studio-row2">
            {number(say('designs.insp.corners'), element.style?.radius ?? 0, (n) => setStyle({ radius: Math.max(0, n) || undefined }), { min: 0, max: 200 })}
            <Toggle label={say('designs.insp.shadow')} checked={element.style?.shadow === true} onChange={(on) => setStyle({ shadow: on || undefined })} />
          </div>
          <Button variant="secondary" size="sm" className="studio-panel__act" onPress={() => onReplace(copyButtonStyle(doc, element))}>
            {say('designs.insp.copyStyle')}
          </Button>
        </>
      )}

      {element.kind === 'calls' && (
        <div className="studio-row2">
          {number(say('designs.insp.columns'), element.options?.columns ?? 2, (n) => setOptions({ columns: n || 1 }), { min: 1, max: 12 })}
          {number(say('designs.insp.rowsN'), element.options?.rows ?? 2, (n) => setOptions({ rows: n || 1 }), { min: 1, max: 12 })}
        </div>
      )}

      {element.kind === 'calls' && (
        <>
          <Toggle label={say('designs.insp.deskNumber')} checked={element.options?.showDesk !== false} onChange={(on) => setOptions({ showDesk: on })} />
          <TextField
            className="studio-field"
            value={String(element.options?.skip ?? 0)}
            onChange={(v) => setOptions({ skip: Math.max(Number(v) || 0, 0) })}
          >
            <Label title={say((element.options?.skip ?? 0) === 0 ? 'designs.insp.skip0' : 'designs.insp.skipN')}>{say('designs.insp.skipFirst')}</Label>
            <Input variant="secondary" type="number" min={0} max={12} />
          </TextField>
        </>
      )}

      {element.kind === 'qr' && (
        <>
          <StudioSelect
            label={say('designs.qr.opens')}
            value={element.options?.format === 'website' ? 'website' : 'follow'}
            onChange={(value) => setOptions(qrFormatPatch(value === 'website' ? 'website' : 'follow'))}
            options={[
              { value: 'follow', label: say('designs.qr.follow') },
              { value: 'website', label: say('designs.qr.website') },
            ]}
          />
          {element.options?.format === 'website' && (
            <TextField className="studio-field" value={element.options?.url ?? ''} onChange={(v) => setOptions({ url: v.trim() || undefined })}>
              <Label>{say('designs.qr.url')}</Label>
              <Input variant="secondary" inputMode="url" maxLength={100} placeholder="https://" />
            </TextField>
          )}
        </>
      )}

      {doc.kind === 'kiosk' && (doc.pages?.length ?? 0) > 1 && onChoosing(element) && (
        <PagesField element={element} document={doc} onChange={(pages) => set({ pages })} />
      )}

      {((doc.kind === 'kiosk' && element.kind !== 'button') || (doc.kind === 'counter' && !COUNTER_HOME[element.kind])) && (
        // All three unless somebody says otherwise, which is what a logo wants.
        <Group label={say('designs.insp.shownOn')}>
          <div className="studio-checks">
            {(doc.kind === 'counter' ? [...COUNTER_SCREENS] : KIOSK_SCREENS).map((screen) => {
              const all = !element.screens || element.screens.length === 0
              const checked = all || element.screens?.includes(screen) === true
              return (
                <Tick
                  key={screen}
                  label={say(SCREEN_NAMES[screen])}
                  checked={checked}
                  onChange={(on) => {
                    const current = all ? (doc.kind === 'counter' ? [...COUNTER_SCREENS] : KIOSK_SCREENS) : [...(element.screens ?? [])]
                    const next = on ? Array.from(new Set([...current, screen])) : current.filter((s) => s !== screen)
                    set({ screens: next })
                  }}
                />
              )
            })}
          </div>
        </Group>
      )}

      {/* An element's own motion plays only when the design's screen change is Slide. */}
      {doc.kind === 'counter' && screenChangeOf(doc.canvas) === 'slide' && (
        <StudioSelect
          label={say('designs.insp.motion')}
          value={motionOf(element)}
          onChange={(value) => onChange(withMotion(element, value as ElementMotion))}
          options={motionChoices(element.kind).map((m) => ({ value: m.value, label: say(m.label) }))}
        />
      )}

      <details className="studio-pos">
        <summary>{say('designs.insp.position')}</summary>
        <div className="studio-pos__grid">
          {(['x', 'y', 'w', 'h'] as const).map((field) => (
            <span key={field}>{number(say(POSITION_KEYS[field]), element[field], (n) => set({ [field]: n }))}</span>
          ))}
          <TextField className="studio-field" value={String(element.z ?? 0)} onChange={(v) => set({ z: Number(v) || 0 })}>
            <Label title={say('designs.insp.depthHint')}>{say('designs.insp.depth')}</Label>
            <Input variant="secondary" type="number" />
          </TextField>
        </div>
      </details>

      <div className="studio-panel__acts">
        {onDuplicate && (
          <Button variant="secondary" size="sm" onPress={() => onDuplicate(element.id)}>
            <Icon name="copy" size={14} />
            {say('designs.page.duplicate')}
          </Button>
        )}
        {onCopy && (
          <Button variant="secondary" size="sm" onPress={() => onCopy(element.id)}>
            {say('designs.clip.copy')}
          </Button>
        )}
        <Button variant="danger-soft" size="sm" onPress={() => onDelete(element.id)}>
          {say('common.remove')}
        </Button>
      </div>
    </section>
  )
}

/** The panel's name and the way back to the design's own settings. */
function PanelHead({ title, named, onDone }: { title: string; named?: string; onDone: () => void }) {
  const { say } = useStaffText()
  return (
    <div className="studio-panel__head">
      <h2 title={named}>{title}</h2>
      <IconButton onPress={onDone} aria-label={say('designs.insp.deselect')}>
        <Icon name="close" size={16} />
      </IconButton>
    </div>
  )
}

/** A labelled group of controls; `hint` is the label's tooltip, drawn under it only as a warning. */
export function Group({ label, hint, warn, children }: { label: string; hint?: string; warn?: boolean; children: ReactNode }) {
  return (
    <fieldset className="studio-field">
      {label && (
        <legend className="studio-field__label" title={hint}>
          {label}
        </legend>
      )}
      {children}
      {hint && warn && (
        <small className="studio-field__hint" data-warn={warn || undefined}>
          {hint}
        </small>
      )}
    </fieldset>
  )
}

/** A row of choices, one pressed: HeroUI's toggle button group. */
export function Seg({
  value,
  options,
  onChange,
  label,
}: {
  value: string
  options: Array<[string, string]>
  onChange: (value: string) => void
  label?: string
}) {
  return (
    <ToggleButtonGroup
      className="studio-seg"
      size="sm"
      selectionMode="single"
      disallowEmptySelection
      aria-label={label}
      selectedKeys={new Set([value])}
      onSelectionChange={(keys) => {
        const next = [...keys][0]
        if (next !== undefined && String(next) !== value) onChange(String(next))
      }}
    >
      {options.map(([v, text]) => (
        <ToggleButton key={v} id={v}>
          {text}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

export function Toggle({ label, checked, onChange }: { label: ReactNode; checked: boolean; onChange: (on: boolean) => void }) {
  return (
    <Switch className="studio-toggle" size="sm" isSelected={checked} onChange={onChange}>
      <Switch.Content>
        <span className="studio-toggle__label">{label}</span>
        <Switch.Control>
          <Switch.Thumb />
        </Switch.Control>
      </Switch.Content>
    </Switch>
  )
}

/**
 * Colours as swatches. Drawn inside the design's own theme (data-theme), so
 * "Strong text" on a dark board is the white it will be on that board, not the
 * near-black it is on a light one. The empty value is the element's default; the
 * last swatch is a literal #rrggbb for the brand colour a customer insists on.
 */
export function Swatches({
  label,
  theme,
  value,
  onPick,
  choices: given,
  none: givenNone,
  paper = false,
}: {
  label: string
  theme: CanvasTheme
  value: string
  onPick: (value: string) => void
  choices?: Array<{ value: string; label: string }>
  none?: string | null
  /** A printed ticket: black and white only, no colour of one's own. */
  paper?: boolean
}) {
  const { say } = useStaffText()
  const choices = given ?? colourChoices(paper ? 'ticket' : 'display').map((c) => ({ value: c.token, label: say(c.label) }))
  const none = givenNone === undefined ? say('designs.page.default') : givenNone
  const row = (
      <div className="studio-swatches" data-theme={theme}>
        {none !== null && (
          <button type="button" className="studio-swatch studio-swatch--none" aria-pressed={value === ''} title={none} aria-label={none} onClick={() => onPick('')} />
        )}
        {choices.map((c) => (
          <button
            key={c.value}
            type="button"
            className="studio-swatch"
            style={{ background: colour(c.value, 'transparent') }}
            aria-pressed={value === c.value}
            title={c.label}
            aria-label={c.label}
            onClick={() => onPick(c.value)}
          />
        ))}
        {/* A ticket coloured before paper allowed only black and white: its colour
            is shown, chosen, so it can be seen and picked away from. */}
        {paper && strayColour(value, choices) && (
          <button
            type="button"
            className="studio-swatch"
            style={{ background: colour(value, 'transparent') }}
            aria-pressed
            title={say('designs.insp.currentColour')}
            aria-label={say('designs.insp.currentColour')}
          />
        )}
        {!paper && <ColourPicker value={value} label={say('designs.insp.anotherColour')} onPick={onPick} />}
      </div>
  )
  // Without a label the row joins its caller's own fieldset.
  return label ? <Group label={label}>{row}</Group> : row
}

/**
 * The panel this design is drawn for.
 *
 * The presets are the panels branches have, the real kiosk's first; anything else
 * is typed and applied in one step, because a width changed before its height has
 * been typed would scale every element twice.
 */
export function SizeField({
  document: doc,
  onResize,
}: {
  document: LayoutDocument
  onResize: (width: number, height: number) => void
}) {
  const { say, lang } = useStaffText()
  const presets = presetsFor(doc.kind)
  const current = presets.find((p) => p.width === doc.canvas.width && p.height === doc.canvas.height)
  const [custom, setCustom] = useState(!current)
  const [width, setWidth] = useState(doc.canvas.width)
  const [height, setHeight] = useState(doc.canvas.height)
  const inRange = (n: number) => n >= MIN_SCREEN_SIDE && n <= MAX_SCREEN_SIDE
  const valid = inRange(width) && inRange(height)
  const changed = width !== doc.canvas.width || height !== doc.canvas.height

  return (
    <div className="studio-field">
      <StudioSelect
        label={say('designs.insp.size')}
        value={custom ? 'custom' : `${doc.canvas.width}x${doc.canvas.height}`}
        onChange={(value) => {
          if (value === 'custom') {
            setCustom(true)
            return
          }
          const preset = presets.find((p) => `${p.width}x${p.height}` === value)
          if (!preset) return
          setCustom(false)
          setWidth(preset.width)
          setHeight(preset.height)
          onResize(preset.width, preset.height)
        }}
        options={[
          ...presets.map((p) => ({ value: `${p.width}x${p.height}`, label: presetLabel(p, lang) })),
          { value: 'custom', label: say('designs.insp.custom') },
        ]}
      />
      {custom && (
        <div className="studio-size">
          <Input
            variant="secondary"
            type="number"
            aria-label={say('designs.insp.width')}
            min={MIN_SCREEN_SIDE}
            max={MAX_SCREEN_SIDE}
            value={width}
            onChange={(e) => setWidth(Number(e.target.value) || 0)}
          />
          <span aria-hidden="true">×</span>
          <Input
            variant="secondary"
            type="number"
            aria-label={say('designs.insp.height')}
            min={MIN_SCREEN_SIDE}
            max={MAX_SCREEN_SIDE}
            value={height}
            onChange={(e) => setHeight(Number(e.target.value) || 0)}
          />
          <Button variant="secondary" size="sm" isDisabled={!valid || !changed} onPress={() => onResize(width, height)}>
            {say('designs.insp.apply')}
          </Button>
        </div>
      )}
    </div>
  )
}

/** A number applied as it is typed; an empty or out-of-range entry waits, and leaving the field shows the value again. */
function NumberInput({ label, value, min, max, onValue }: { label: string; value: number; min?: number; max?: number; onValue: (n: number) => void }) {
  const [draft, setDraft] = useState<string | null>(null)
  return (
    <TextField
      className="studio-field"
      value={draft ?? String(value)}
      onChange={(raw) => {
        setDraft(raw)
        const n = Number(raw)
        if (raw.trim() !== '' && Number.isFinite(n) && (min === undefined || n >= min) && (max === undefined || n <= max)) onValue(n)
      }}
      onBlur={() => setDraft(null)}
    >
      <Label>{label}</Label>
      <Input variant="secondary" type="number" min={min} max={max} />
    </TextField>
  )
}

function onChoosing(el: LayoutElement): boolean {
  return !el.screens || el.screens.length === 0 || el.screens.includes('choosing')
}

/** Which menu pages an element is on: all of them, which is what a header wants,
 *  or the ones ticked. */
function PagesField({
  element,
  document: doc,
  onChange,
}: {
  element: LayoutElement
  document: LayoutDocument
  onChange: (pages: string[] | undefined) => void
}) {
  const { say, lang } = useStaffText()
  const pages = withPages(doc).pages!
  const all = !element.pages || element.pages.length === 0
  return (
    <Group label={say('designs.insp.onPages')}>
      <div className="studio-checks">
        {pages.map((p) => {
          const checked = all || element.pages!.includes(p.id)
          return (
            <Tick
              key={p.id}
              label={pageLabel(doc, p.id, lang)}
              checked={checked}
              onChange={(on) => {
                const current = all ? pages.map((q) => q.id) : [...element.pages!]
                const next = on ? Array.from(new Set([...current, p.id])) : current.filter((id) => id !== p.id)
                if (next.length === 0) return
                // Every page written out is "every page", and stored as such, so
                // a page added later shows it too.
                onChange(next.length === pages.length ? undefined : next)
              }}
            />
          )
        })}
      </div>
    </Group>
  )
}

/** The welcome screen's own setting: whether the kiosk starts on it or on the menu. */
export function WelcomePanel({ document: doc, onReplace }: { document: LayoutDocument; onReplace: (doc: LayoutDocument) => void }) {
  const { say } = useStaffText()
  return (
    <section className="studio-panel">
      <div className="studio-panel__head">
        <h2>{say('designs.ed.welcome')}</h2>
      </div>
      <Toggle
        label={say('designs.ed.startWelcome')}
        checked={!doc.canvas.skipWelcome}
        onChange={(on) => onReplace({ ...doc, canvas: { ...doc.canvas, skipWelcome: on ? undefined : true } })}
      />
    </section>
  )
}

/** The page being drawn on: its name in three languages, and a way to delete it.
 *  The menu has a name and cannot be deleted. */
export function PagePanel({ document: doc, page, onReplace }: { document: LayoutDocument; page: string; onReplace: (doc: LayoutDocument) => void }) {
  // Asked in the panel rather than in a browser box, which stops everything on the page.
  const { say } = useStaffText()
  const editLangs = designLanguages(doc, useCustomerLanguages()).map((c) => [c, nativeName(c)] as const)
  const [confirming, setConfirming] = useState(false)
  const pages = withPages(doc).pages!
  const it = pages.find((p) => p.id === page)
  if (!it) return null
  const isMenu = pages[0]!.id === page
  const openers = doc.elements.filter((el) => el.action?.kind === 'page' && el.action.pageId === page).length
  return (
    <section className="studio-panel">
      <div className="studio-panel__head">
        <h2 title={say(isMenu ? 'designs.insp.menuHint' : 'designs.insp.pageHint')}>{say(isMenu ? 'designs.ed.menu' : 'designs.ed.page')}</h2>
      </div>
      {editLangs.map(([lang, name]) => (
        <TextField key={lang} className="studio-field" value={it.name?.[lang] ?? ''} onChange={(v) => onReplace(renamePage(doc, page, lang, v))}>
          <Label>{name}</Label>
          <Input variant="secondary" />
        </TextField>
      ))}
      {!isMenu &&
        (confirming ? (
          <div className="studio-confirm" role="alert">
            <span>{say('designs.insp.openers', { n: openers })}</span>
            <span className="studio-confirm__actions">
              <Button variant="tertiary" size="sm" onPress={() => setConfirming(false)}>
                {say('common.keep')}
              </Button>
              <Button variant="danger" size="sm" onPress={() => onReplace(deletePage(doc, page))}>
                {say('common.delete')}
              </Button>
            </span>
          </div>
        ) : (
          <Button
            variant="danger-soft"
            size="sm"
            className="studio-panel__act"
            onPress={() => (openers === 0 ? onReplace(deletePage(doc, page)) : setConfirming(true))}
          >
            {say('designs.insp.deletePage')}
          </Button>
        ))}
    </section>
  )
}

/** Every button on the page this one is on given its look: background, text,
 *  border, corners, shadow and weight. Not its words, its picture or what it does. */
function copyButtonStyle(doc: LayoutDocument, source: LayoutElement): LayoutDocument {
  const sharesPage = (el: LayoutElement) => {
    const a = source.pages ?? []
    const b = el.pages ?? []
    return a.length === 0 || b.length === 0 || a.some((p) => b.includes(p))
  }
  return {
    ...doc,
    elements: doc.elements.map((el) =>
      el.kind === 'button' && el.id !== source.id && sharesPage(el)
        ? { ...el, style: { ...source.style } }
        : el,
    ),
  }
}


/** A customer text in the chosen language, or its one value when it is the same in every language. */
function CustomerWords({
  label,
  hint,
  text,
  language,
  several,
  defaultLang,
  multiline,
  placeholder,
  extra,
  onChange,
}: {
  label: string
  hint?: string
  text: LayoutText | undefined
  language: string
  several: boolean
  defaultLang: string
  multiline?: boolean
  placeholder?: string
  /** Beside the label: the button title's Reset. */
  extra?: ReactNode
  onChange: (text: LayoutText) => void
}) {
  const same = isSame(text)
  const head = <Label title={hint}>{same ? label : textLabel(label, language, several)}</Label>
  return (
    <>
      <TextField className="studio-field" value={ownWords(text, language)} onChange={(v) => onChange(withWords(text, language, v, defaultLang))}>
        {extra ? (
          <span className="studio-between">
            {head}
            {extra}
          </span>
        ) : (
          head
        )}
        {multiline ? <TextArea variant="secondary" rows={4} /> : <Input variant="secondary" placeholder={placeholder} />}
      </TextField>
      {(several || same) && <SameSwitch value={text} onChange={onChange} defaultLang={defaultLang} label={label} />}
    </>
  )
}

/** A button's own settings: what it does, what it says, and a picture filling it. */
function without(text: LayoutText | undefined, lang: string): LayoutText | undefined {
  const { [lang]: _gone, ...rest } = text ?? {}
  return Object.keys(rest).length > 0 ? rest : undefined
}

function ButtonFields({
  element,
  document: doc,
  services,
  assets,
  uploading,
  uploadError,
  onUpload,
  onChange,
  language,
  several,
  defaultLang,
}: {
  element: LayoutElement
  document: LayoutDocument
  language: string
  several: boolean
  defaultLang: string
  services?: Service[]
  assets: LayoutAsset[]
  uploading: boolean
  uploadError: string
  onUpload: (file: File) => void
  onChange: (element: LayoutElement) => void
}) {
  const { say, lang: reader } = useStaffText()
  const action = element.action
  const kind = action?.kind ?? ''
  const pages = withPages(doc).pages!
  const here = element.pages ?? []
  const set = (patch: Partial<LayoutElement>) => onChange({ ...element, ...patch })
  const setAction = (next: ButtonAction | undefined) => set({ action: next })
  const active = (services ?? []).filter((s) => s.active)
  // What the button says when its own words are left empty, shown as the
  // placeholder so a designer sees it will not be blank.
  const fallback = (lang: string) =>
    action?.kind === 'service'
      ? serviceName(active.find((s) => s.id === action.serviceId)?.name ?? {}, lang as 'az', '')
      : action?.kind === 'page'
        ? textIn(pages.find((p) => p.id === action.pageId)?.name, lang)
        : action?.kind === 'back'
          ? t('designs.page.back', lang === 'ru' || lang === 'en' ? lang : 'az')
          : action?.kind === 'booking'
            ? kioskSay('haveBooking', lang as Language)
            : ''
  const owned = isSame(element.content)
    ? ownWords(element.content, language).trim() !== ''
    : element.content !== undefined && Object.prototype.hasOwnProperty.call(element.content, language)

  return (
    <>
      <Group label={say('designs.insp.does')} hint={!action ? say('designs.insp.doesNothing') : undefined} warn>
        <Seg
          label={say('designs.insp.does')}
          value={kind}
          options={[
            ['service', say('designs.page.service')],
            ['page', say('designs.ed.page')],
            ['back', say('designs.page.back')],
            ['booking', say('designs.page.booking')],
          ]}
          onChange={(value) => {
            if (value === 'service') setAction({ kind: 'service', serviceId: active[0]?.id ?? '' })
            else if (value === 'page') {
              const target = pages.find((p) => !here.includes(p.id) && p.id !== pages[0]!.id) ?? pages.find((p) => !here.includes(p.id))
              setAction(target ? { kind: 'page', pageId: target.id } : undefined)
            } else if (value === 'booking') setAction({ kind: 'booking' })
            else setAction({ kind: 'back' })
          }}
        />
      </Group>

      {action?.kind === 'service' && (
        <StudioSelect
          label={say('designs.page.service')}
          value={action.serviceId}
          onChange={(value) => setAction({ kind: 'service', serviceId: value })}
          options={active.map((s) => ({ value: s.id, label: serviceName(s.name, PREVIEW_LANGUAGE, s.code) }))}
        />
      )}

      {action?.kind === 'page' && (
        <StudioSelect
          label={say('designs.insp.opens')}
          value={action.pageId}
          onChange={(value) => setAction({ kind: 'page', pageId: value })}
          options={pages.filter((p) => !here.includes(p.id)).map((p) => ({ value: p.id, label: pageLabel(doc, p.id, reader) }))}
        />
      )}

      <CustomerWords
        label={say('designs.insp.title')}
        text={element.content}
        language={language}
        several={several}
        defaultLang={defaultLang}
        placeholder={owned ? undefined : fallback(language)}
        onChange={(content) => set({ content })}
        extra={
          // A title typed or cleared here is the button's own; Reset brings back the default.
          owned && fallback(language) ? (
            <Button
              variant="ghost"
              size="sm"
              onPress={() => set({ content: isSame(element.content) ? undefined : without(element.content, language) })}
            >
              {say('designs.word.reset')}
            </Button>
          ) : undefined
        }
      />

      <CustomerWords
        label={say('designs.insp.secondLine')}
        text={element.subtitle}
        language={language}
        several={several}
        defaultLang={defaultLang}
        onChange={(subtitle) => set({ subtitle })}
      />

      <Group label={say('designs.el.image')} hint={say('designs.insp.pictureHint')}>
        <div className="studio-stack">
          <StudioSelect
            aria-label={say('designs.el.image')}
            value={element.assetId ?? ''}
            onChange={(value) => set({ assetId: value || undefined, icon: undefined })}
            options={[
              { value: '', label: say('designs.insp.none') },
              ...assets.filter((asset) => asset.contentType.startsWith('image/')).map((asset) => ({ value: asset.id, label: asset.filename })),
            ]}
          />
          <StudioFilePicker accept="image/png,image/jpeg,image/gif,.png,.jpg,.jpeg,.gif" label={say('designs.media.upload')} disabled={uploading} onFile={onUpload} />
        </div>
      </Group>
      {uploadError && <Notice>{uploadError}</Notice>}
    </>
  )
}
